import { useState } from 'react'
import { useTheme } from '../contexts/ThemeContext'

type Restaurant = 'woodfire' | 'fastsushi' | 'scoop' | 'thaihouse'
type ColorMode = 'muted' | 'colorful'
type Theme = 'light' | 'dark'

export default function SimpleTest() {
  const { setTheme } = useTheme()
  const [selectedRestaurant, setSelectedRestaurant] = useState<Restaurant>('woodfire')
  const [selectedColorMode, setSelectedColorMode] = useState<ColorMode>('muted')
  const [selectedTheme, setSelectedTheme] = useState<Theme>('light')

  const restaurants: { value: Restaurant; label: string }[] = [
    { value: 'woodfire', label: 'Woodfire Pizza' },
    { value: 'fastsushi', label: 'Fast Sushi' },
    { value: 'scoop', label: 'Scoop Ice Cream' },
    { value: 'thaihouse', label: 'Thai House' }
  ]

  const colorModes: { value: ColorMode; label: string }[] = [
    { value: 'muted', label: 'Muted' },
    { value: 'colorful', label: 'Colorful' }
  ]

  const themes: { value: Theme; label: string }[] = [
    { value: 'light', label: 'Light' },
    { value: 'dark', label: 'Dark' }
  ]

  const handleThemeChange = (restaurant: Restaurant, colorMode: ColorMode, theme: Theme) => {
    const themeKey = `${colorMode}${theme}` as 'mutedlight' | 'colorfullight' | 'muteddark' | 'colorfuldark'
    setTheme(restaurant, themeKey)
  }

  const handleRestaurantChange = (restaurant: Restaurant) => {
    setSelectedRestaurant(restaurant)
    handleThemeChange(restaurant, selectedColorMode, selectedTheme)
  }

  const handleColorModeChange = (colorMode: ColorMode) => {
    setSelectedColorMode(colorMode)
    handleThemeChange(selectedRestaurant, colorMode, selectedTheme)
  }

  const handleThemeChange2 = (theme: Theme) => {
    setSelectedTheme(theme)
    handleThemeChange(selectedRestaurant, selectedColorMode, theme)
  }

  return (
    <div className="min-h-screen bg-surface-primary p-8">
      <div className="max-w-7xl mx-auto">
        <h1 className="text-4xl font-bold text-content-primary mb-8">Theme Switcher Test</h1>
        
        <div className="bg-surface-secondary rounded-lg p-6 mb-8">
          <h2 className="text-2xl font-semibold text-content-primary mb-6">Theme Controls</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <label className="block text-sm font-medium text-content-secondary mb-3">
                Restaurant Brand
              </label>
              <div className="space-y-2">
                {restaurants.map(({ value, label }) => (
                  <button
                    key={value}
                    onClick={() => handleRestaurantChange(value)}
                    className={`w-full px-4 py-2 rounded-lg text-left transition-colors ${
                      selectedRestaurant === value
                        ? 'bg-primary-500 text-white'
                        : 'bg-surface-tertiary text-content-primary hover:bg-surface-primary'
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-content-secondary mb-3">
                Color Mode
              </label>
              <div className="space-y-2">
                {colorModes.map(({ value, label }) => (
                  <button
                    key={value}
                    onClick={() => handleColorModeChange(value)}
                    className={`w-full px-4 py-2 rounded-lg text-left transition-colors ${
                      selectedColorMode === value
                        ? 'bg-primary-500 text-white'
                        : 'bg-surface-tertiary text-content-primary hover:bg-surface-primary'
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-content-secondary mb-3">
                Theme
              </label>
              <div className="space-y-2">
                {themes.map(({ value, label }) => (
                  <button
                    key={value}
                    onClick={() => handleThemeChange2(value)}
                    className={`w-full px-4 py-2 rounded-lg text-left transition-colors ${
                      selectedTheme === value
                        ? 'bg-primary-500 text-white'
                        : 'bg-surface-tertiary text-content-primary hover:bg-surface-primary'
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="mt-6 p-4 bg-surface-tertiary rounded-lg">
            <p className="text-sm text-content-secondary">
              Current theme: <span className="font-mono font-semibold text-content-primary">
                {selectedRestaurant}/{selectedColorMode}{selectedTheme}
              </span>
            </p>
          </div>
        </div>

        <div className="bg-surface-secondary rounded-lg p-6">
          <h2 className="text-2xl font-semibold text-content-primary mb-4">Test Content</h2>
          <p className="text-content-primary mb-2">This is a simple test page to verify theme switching is working.</p>
          <p className="text-content-secondary">The colors should change when you switch themes above.</p>
          
          <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-primary-500 text-white p-4 rounded">Primary 500</div>
            <div className="bg-primary-700 text-white p-4 rounded">Primary 700</div>
            <div className="bg-accent-500 text-white p-4 rounded">Accent 500</div>
            <div className="bg-surface-tertiary text-content-primary p-4 rounded">Surface Tertiary</div>
          </div>
        </div>
      </div>
    </div>
  )
}